import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';

import { useUpdateProjectMutation } from 'features/master/api/master.api';
import type { ProjectDetail, ProjectSubsidyConfig } from 'features/master/api/types';
import { useGetGlobalPromptSectionsQuery } from 'features/projects/api/promptSections.api';
import type { GlobalPromptSection } from 'features/projects/api/promptSections.api';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import {
  AGENT_PROMPT_FIELDS,
  type AgentPromptField,
  DEFAULT_PROMPT_OPTION,
  buildPromptSectionField,
  readPromptSectionName,
} from '../model/mortgageRules';

export interface SubProjectPrompts {
  id: number;
  name: string;
  financialAdvisorAgentPrompt?: string | null;
  mortgageAgentPrompt?: string | null;
  subsidyConfig?: ProjectSubsidyConfig | null;
}

interface Props {
  selectedIds: number[];
  subProjects: SubProjectPrompts[];
}

interface PickerProps extends Props {
  field: AgentPromptField;
}

/* Each agent offers only the sections tagged with its own prompt category, split by the subsidy tag. */
const SECTIONS_PAGE_SIZE = 200;

/* Typed against the project shape the mutation patches, so a renamed column fails here and not in
   a silent no-op request. */
type AgentPromptPatch = Partial<Pick<ProjectDetail, AgentPromptField['key']>>;

interface ComboboxProps {
  id: string;
  value: string;
  groups: { label: string; items: GlobalPromptSection[] }[];
  disabled: boolean;
  onChange: (value: string) => void;
}

/* Searchable select, same popover pattern as ExternalLinkConfigSection. */
const PromptSectionCombobox = ({ id, value, groups, disabled, onChange }: ComboboxProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();
  const matches = (section: GlobalPromptSection): boolean =>
    section.name.toLowerCase().includes(query) || section.displayName.toLowerCase().includes(query);
  const visibleGroups = groups
    .map((group) => ({ ...group, items: group.items.filter(matches) }))
    .filter((group) => group.items.length > 0);
  const showDefault = 'por defecto'.includes(query);
  const selected = groups.flatMap((group) => group.items).find((section) => section.name === value);
  const label = value === DEFAULT_PROMPT_OPTION ? 'Por defecto' : selected?.displayName || value;

  const pick = (next: string): void => {
    onChange(next);
    setOpen(false);
    setSearch('');
  };

  const option = (optionValue: string, text: string): ReactElement => (
    <button
      type="button"
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-bg-surface-alt',
        optionValue === value && 'bg-bg-surface-alt font-medium'
      )}
      onClick={() => pick(optionValue)}
    >
      <Check className={cn('size-4 shrink-0', optionValue === value ? 'opacity-100' : 'opacity-0')} />
      <span className="truncate">{text}</span>
    </button>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          disabled={disabled}
          className="w-full justify-between font-normal"
          aria-expanded={open}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) gap-0 p-0">
        <div className="flex items-center gap-2 border-b border-border-subtle px-2.5 py-1.5">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar prompt…"
            className="h-6 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        {!showDefault && visibleGroups.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-text-subtle">Sin resultados.</p>
        ) : (
          <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto p-1">
            {showDefault && option(DEFAULT_PROMPT_OPTION, 'Por defecto')}
            {visibleGroups.map((group) => (
              <div key={group.label} className="border-t border-border-subtle pt-1">
                <p className="px-2 pb-1 pt-1.5 text-xs font-medium text-text-subtle">{group.label}</p>
                {group.items.map((section) => (
                  <Fragment key={section.id}>{option(section.name, section.displayName || section.name)}</Fragment>
                ))}
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/* One agent's prompt with its own apply, so changing one never rewrites the other. */
const AgentPromptPicker = ({ field, selectedIds, subProjects }: PickerProps): ReactElement => {
  const [updateProject, { isLoading: isSaving }] = useUpdateProjectMutation();
  const { data: sections, isLoading: areSectionsLoading } = useGetGlobalPromptSectionsQuery({
    offset: 0,
    limit: SECTIONS_PAGE_SIZE,
    categories: [field.sectionCategory],
  });
  const options = sections?.globalPromptSections ?? [];
  const groups = [
    { label: 'Sin subsidio', items: options.filter((section) => !section.categories.includes('subsidio')) },
    { label: 'Con subsidio', items: options.filter((section) => section.categories.includes('subsidio')) },
  ].filter((group) => group.items.length > 0);
  const hasSelection = selectedIds.length > 0;

  /* Seeded from the first selected sub-project, so a single-row change is edited and not retyped. */
  const seed = useMemo(
    () => readPromptSectionName(subProjects.find((subProject) => selectedIds.includes(subProject.id))?.[field.key]),
    [field.key, selectedIds, subProjects]
  );

  const [draft, setDraft] = useState(seed);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(seed);
  }, [seed]);

  const changed = draft !== seed;

  /* Only the rows that would end up different get patched, so the count says what will actually
     change rather than how many are ticked: the selection usually includes some that already run it. */
  const idsToPatch = changed
    ? subProjects
        .filter(
          (subProject) => selectedIds.includes(subProject.id) && readPromptSectionName(subProject[field.key]) !== draft
        )
        .map((subProject) => subProject.id)
    : [];

  const canApply = idsToPatch.length > 0 && !isSaving;

  const handleApply = async (): Promise<void> => {
    if (!canApply) return;
    setError(null);

    const patch: AgentPromptPatch = { [field.key]: buildPromptSectionField(draft) };

    try {
      /* One PATCH per project. Sequential on purpose: a partial failure then stops at the first one
         instead of leaving an unknown half of the selection written. */
      for (const id of idsToPatch) {
        await updateProject({ id, patch }).unwrap();
      }
    } catch (saveError) {
      setError(extractErrorMessage(saveError, 'No se pudo guardar el prompt en los subproyectos seleccionados.'));
    }
  };

  const applyCopy = ((): string | null => {
    if (!hasSelection) return null;
    if (!changed) return 'Cambia el prompt para poder aplicarlo.';
    if (idsToPatch.length === 0) return 'Los seleccionados ya corren ese prompt.';
    const scope = `${idsToPatch.length} ${idsToPatch.length === 1 ? 'subproyecto' : 'subproyectos'}`;
    return idsToPatch.length === selectedIds.length
      ? `Se aplicará a ${scope}.`
      : `Se aplicará a ${scope} de los ${selectedIds.length} seleccionados; el resto ya lo tiene.`;
  })();

  return (
    <div className="flex flex-col space-y-2 rounded-md border border-border p-4">
      <div className={cn('space-y-2', !hasSelection && 'opacity-50')}>
        <Label htmlFor={field.key}>{field.label}</Label>
        <PromptSectionCombobox
          id={field.key}
          value={draft}
          groups={groups}
          disabled={!hasSelection || isSaving || areSectionsLoading}
          onChange={setDraft}
        />
        <p className="text-xs text-text-muted">{field.description}</p>
      </div>

      <div className="mt-auto flex items-center gap-2 pt-3">
        <Button type="button" size="sm" disabled={!canApply} onClick={() => void handleApply()}>
          {isSaving ? 'Guardando...' : 'Aplicar'}
        </Button>
        {applyCopy && <span className="text-xs text-text-muted">{applyCopy}</span>}
      </div>

      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}
    </div>
  );
};

/*
 * Which prompt each agent runs, for the sub-projects selected in the table. The field holds a
 * `<GLOBALSECTION:...>` tag and the text lives in the section, so this writes the pointer and never
 * the prompt. Unlike its neighbours it cannot go through the bulk endpoint — that one writes business
 * rules, and this is a column on the project — so it patches one project per selected row.
 */
const MortgageAgentPromptsCard = ({ selectedIds, subProjects }: Props): ReactElement => {
  const hasSelection = selectedIds.length > 0;

  return (
    <section className="flex flex-col space-y-3 rounded-md border border-border p-6 lg:col-span-2">
      <div className={cn('space-y-1', !hasSelection && 'opacity-50')}>
        <p className="text-sm font-medium">Prompts de los agentes</p>
        <p className="text-xs text-text-muted">
          Qué sección global usa cada agente en los subproyectos seleccionados. El texto se edita en la sección; acá
          solo eliges cuál corre.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {AGENT_PROMPT_FIELDS.map((field) => (
          <AgentPromptPicker key={field.key} field={field} selectedIds={selectedIds} subProjects={subProjects} />
        ))}
      </div>
    </section>
  );
};

export default MortgageAgentPromptsCard;
